import { useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import AMapLoader from "@amap/amap-jsapi-loader";

const createPinSvg = (color: string, text: string) => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="44" height="52" viewBox="0 0 44 52"><defs><filter id="s" x="-20%" y="-20%" width="140%" height="140%"><feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="#000" flood-opacity="0.25"/></filter></defs><g filter="url(#s)"><path d="M22 3C12.611 3 5 10.611 5 20c0 12.75 17 29 17 29s17-16.25 17-29C39 10.611 31.389 3 22 3z" fill="${color}"/><circle cx="22" cy="20" r="12" fill="#fff"/></g><text x="22" y="24" text-anchor="middle" font-size="12" font-family="Arial, sans-serif" font-weight="700" fill="${color}">${text}</text></svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

declare global {
  interface Window {
    AMap: any;
    _AMapSecurityConfig?: {
      securityJsCode: string;
    };
  }
}

export interface AMapComponentRef {
  getMap: () => any;
  focusPoint: (lng: number, lat: number, zoom?: number) => void;
  planRoute: (
    start: [number, number],
    end: [number, number],
    waypoints?: [number, number][],
  ) => void;
  clearRoute: () => void;
  addMiddleMarker: (lng: number, lat: number) => void;
  clearMiddleMarkers: () => void;
  addRiskMarker: (lng: number, lat: number, risk: boolean) => void;
  clearRiskMarkers: () => void;
  addStartMarker: (lng: number, lat: number) => void;
  addEndMarker: (lng: number, lat: number) => void;
  clearStartEndMarkers: () => void;
}

export type RouteInfo = {
  distance: number;
  duration: number;
};

interface AMapComponentProps {
  className?: string;
  style?: React.CSSProperties;
  onLoad?: () => void;
  onRouteInfoChange?: (info: RouteInfo | null) => void;
}

export const AMapComponent = forwardRef<AMapComponentRef, AMapComponentProps>(
  ({ className, style, onLoad, onRouteInfoChange }, ref) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const riskMarkersRef = useRef<any[]>([]);
    const middleMarkersRef = useRef<any[]>([]);
    const startEndMarkersRef = useRef<any[]>([]);
    const routeRef = useRef<any>(null);
    const amapRef = useRef<any>(null);

    useImperativeHandle(ref, () => ({
      getMap: () => amapRef.current,

      focusPoint: (lng: number, lat: number, zoom = 15) => {
        if (!amapRef.current) {
          return;
        }
        amapRef.current.setCenter([lng, lat]);
        amapRef.current.setZoom(zoom);
      },

      planRoute: (
        start: [number, number],
        end: [number, number],
        waypoints: [number, number][] = [],
      ) => {
        if (!amapRef.current || !window.AMap) {
          return;
        }

        onRouteInfoChange?.(null);

        if (routeRef.current) {
          routeRef.current.clear();
          routeRef.current = null;
        }

        const driving = new window.AMap.Driving({
          map: amapRef.current,
          hideMarkers: true,
          showTraffic: true,
          autoFitView: true,
        });

        const startLngLat = new window.AMap.LngLat(start[0], start[1]);
        const endLngLat = new window.AMap.LngLat(end[0], end[1]);
        const waypointLngLats = waypoints.map(
          (point) => new window.AMap.LngLat(point[0], point[1]),
        );

        driving.search(
          startLngLat,
          endLngLat,
          {
            waypoints: waypointLngLats,
          },
          (status: string, result: any) => {
            if (status === "complete" && result?.routes?.length) {
              routeRef.current = driving;
              const route = result.routes[0];
              const routeDistance = route?.distance ?? 0;
              onRouteInfoChange?.({
                distance: routeDistance,
                duration: route?.time ?? 0,
              });

              const maxZoom = routeDistance < 3000 ? 18 : routeDistance < 10000 ? 16 : 16;
              amapRef.current?.setFitView(undefined, false, [60, 40, 40, 40], maxZoom);
            } else {
              console.error("路线规划失败:", result);
              onRouteInfoChange?.(null);
              routeRef.current = null;
            }
          },
        );
      },

      clearRoute: () => {
        if (routeRef.current) {
          routeRef.current.clear();
          routeRef.current = null;
        }
        onRouteInfoChange?.(null);
      },

      addMiddleMarker: (lng: number, lat: number) => {
        if (!amapRef.current || !window.AMap) {
          return;
        }

        const marker = new window.AMap.Marker({
          position: [lng, lat],
          title: "途经点",
          icon: new window.AMap.Icon({
            size: new window.AMap.Size(44, 52),
            image: createPinSvg("#2563eb", "途"),
            imageSize: new window.AMap.Size(44, 52),
          }),
          offset: new window.AMap.Pixel(-22, -52),
        });

        amapRef.current.add(marker);
        middleMarkersRef.current.push(marker);
      },

      clearMiddleMarkers: () => {
        if (!amapRef.current) {
          return;
        }

        middleMarkersRef.current.forEach((marker) => {
          amapRef.current.remove(marker);
        });
        middleMarkersRef.current = [];
      },

      addRiskMarker: (lng: number, lat: number, risk: boolean) => {
        if (!risk || !amapRef.current || !window.AMap) {
          return;
        }

        const marker = new window.AMap.Marker({
          position: [lng, lat],
          icon: new window.AMap.Icon({
            size: new window.AMap.Size(30, 30),
            image: "https://vdata.amap.com/icons/b18/1/2.png",
            imageSize: new window.AMap.Size(30, 30),
          }),
          offset: new window.AMap.Pixel(-15, -15),
          title: "风险路段",
        });

        amapRef.current.add(marker);
        riskMarkersRef.current.push(marker);
      },

      clearRiskMarkers: () => {
        if (amapRef.current) {
          riskMarkersRef.current.forEach((marker) => {
            amapRef.current.remove(marker);
          });
          riskMarkersRef.current = [];
        }
      },

      addStartMarker: (lng: number, lat: number) => {
        if (amapRef.current && window.AMap) {
          startEndMarkersRef.current = startEndMarkersRef.current.filter((marker) => {
            const isStart = marker.getExtData && marker.getExtData() === "start";
            if (isStart) {
              amapRef.current.remove(marker);
            }
            return !isStart;
          });

          const marker = new window.AMap.Marker({
            position: [lng, lat],
            title: "起点",
            icon: new window.AMap.Icon({
              size: new window.AMap.Size(44, 52),
              image: createPinSvg("#16a34a", "起"),
              imageSize: new window.AMap.Size(44, 52),
            }),
            offset: new window.AMap.Pixel(-22, -52),
            extData: "start",
          });
          amapRef.current.add(marker);
          startEndMarkersRef.current.push(marker);
        }
      },

      addEndMarker: (lng: number, lat: number) => {
        if (amapRef.current && window.AMap) {
          startEndMarkersRef.current = startEndMarkersRef.current.filter((marker) => {
            const isEnd = marker.getExtData && marker.getExtData() === "end";
            if (isEnd) {
              amapRef.current.remove(marker);
            }
            return !isEnd;
          });

          const marker = new window.AMap.Marker({
            position: [lng, lat],
            title: "终点",
            icon: new window.AMap.Icon({
              size: new window.AMap.Size(44, 52),
              image: createPinSvg("#dc2626", "终"),
              imageSize: new window.AMap.Size(44, 52),
            }),
            offset: new window.AMap.Pixel(-22, -52),
            extData: "end",
          });
          amapRef.current.add(marker);
          startEndMarkersRef.current.push(marker);
        }
      },

      clearStartEndMarkers: () => {
        if (amapRef.current) {
          startEndMarkersRef.current.forEach((marker) => {
            amapRef.current.remove(marker);
          });
          startEndMarkersRef.current = [];
        }
      },
    }));

    useEffect(() => {
      let mounted = true;

      window._AMapSecurityConfig = {
        securityJsCode: "e39abc8cafb4e2f42ad123c42e46fed1",
      };

      AMapLoader.load({
        key: import.meta.env.VITE_AMAP_KEY,
        version: "2.0",
        plugins: ["AMap.Marker", "AMap.Driving"],
      })
        .then((AMap) => {
          if (!mounted || !containerRef.current) return;

          amapRef.current = new AMap.Map(containerRef.current, {
            zoom: 10,
            center: [112.5266, 27.91507],
            mapStyle: "amap://styles/whitesmoke",
            zoomEnable: true,
            dragEnable: true,
            doubleClickZoom: true,
            keyboardEnable: false,
            resizeEnable: true,
          });

          onLoad?.();
        })
        .catch((e) => {
          console.error("AMap 加载失败:", e);
        });

      return () => {
        mounted = false;

        if (routeRef.current) {
          routeRef.current.clear();
          routeRef.current = null;
        }

        if (amapRef.current) {
          amapRef.current.destroy();
          amapRef.current = null;
        }

        riskMarkersRef.current = [];
        middleMarkersRef.current = [];
        startEndMarkersRef.current = [];
      };
    }, []);

    return (
      <div
        ref={containerRef}
        className={className}
        style={{ width: "100%", height: "100%", ...style }}
      ></div>
    );
  },
);

AMapComponent.displayName = "AMapComponent";
